// How a dashboard row involves the viewer, beyond being its author, as
// GitHub's own reviewRequests, assignees and viewerLatestReview say. What
// is the viewer's turn is a review asked of them and the pull request
// assigned to them, then an assignment alone; a review asked without an
// assignment, a team's request, or a review they gave is worth knowing and
// not their turn. A team's request is told as one: GitHub doesn't say who
// is in the team.
import type { DashPr } from '../api/queries';
import { sameLogin } from './attention';

export interface Involvement {
  /** A review was asked of the viewer by name. */
  requested: boolean;
  /** The pull request is assigned to the viewer. */
  assigned: boolean;
  /** A review was asked of a team (the viewer's, when a review-requested:@me search found it). */
  team: boolean;
  /** The viewer reviewed it before. */
  reviewed: boolean;
}

type Involving = Pick<DashPr, 'author' | 'reviewRequests' | 'assignees' | 'viewerLatestReview'>;

/**
 * How `pr` involves `me`; null when none of it applies, or for their own
 * pull request (assigning it to themselves says nothing new). A row saved
 * before assignees or the viewer's review were read has none of them.
 */
export function involvement(pr: Involving, me: string): Involvement | null {
  if (!me || (pr.author && sameLogin(pr.author.login, me))) return null;
  const reviewers = pr.reviewRequests.nodes.map((n) => n.requestedReviewer).filter((r) => !!r);
  const requested = reviewers.some((r) => 'login' in r && !!r.login && sameLogin(r.login, me));
  const team = reviewers.some((r) => r.__typename === 'Team');
  const assigned = (pr.assignees?.nodes ?? []).some((a) => sameLogin(a.login, me));
  const reviewed = !!pr.viewerLatestReview && pr.viewerLatestReview.state !== 'PENDING';
  return requested || assigned || team || reviewed ? { requested, assigned, team, reviewed } : null;
}

/** The chip's words: the strongest of how the row involves the viewer. */
export function involvementText(i: Involvement): string {
  if (i.requested && i.assigned) return 'review requested · assigned';
  if (i.assigned) return 'assigned';
  if (i.requested) return 'review requested';
  if (i.team) return 'team review requested';
  return 'reviewed';
}

/** The chip's title, a sentence for the pointer and screen readers. */
export function involvementTitle(i: Involvement): string {
  if (i.requested && i.assigned) return 'Your review is requested, and the pull request is assigned to you: your turn';
  if (i.assigned) return 'The pull request is assigned to you; your review is not requested';
  const not = 'It is not assigned to you, so not your turn';
  if (i.requested) return `Your review is requested. ${not}`;
  if (i.team) return `A team's review is requested. ${not}`;
  return `You reviewed it before. ${not}`;
}

/**
 * A section's rows in the order the viewer should take them: those with a
 * review asked of them and assigned to them first, the rest after, each
 * part in the order it came (most recently updated first). A stack goes
 * where its first row goes (logic/stacks.ts `stackSection`).
 */
export function byInvolvement<R extends { pr: Involving }>(rows: readonly R[], me: string): R[] {
  const first = (r: R) => {
    const i = involvement(r.pr, me);
    return !!i && i.requested && i.assigned;
  };
  return [...rows.filter(first), ...rows.filter((r) => !first(r))];
}
